import React, { useContext, useMemo, useState } from 'react';
import { Button, Dropdown } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy, faRightLeft, faTrash, faXmark, faListCheck } from '@fortawesome/free-solid-svg-icons';
import { useAppDispatch, useAppSelector } from '../store';
import { setSelectionMode, clearSelection, setSelectedGameIds } from '../reducers/uiReducer';
import { fetchGroups } from '../reducers/groupsReducer';
import { getGames } from '../reducers/gamesReducer';
import { GroupModel, GroupKind } from '../types/GroupModel';
import GroupsAPI from '../api/GroupsAPI';
import GamesAPI from '../api/GamesAPI';
import AppContext from '../context/AppContext';

// Bulk game management. Enter selection mode, tick games in the list, then add them to a group,
// move them from the group you're viewing into another, or remove them from the current group.
export default function GamesBulkBar(): React.ReactNode {
  const dispatch = useAppDispatch();
  const { isAuthenticated, isMobile } = useContext(AppContext);
  const { games } = useAppSelector((state) => state.gamesReducer);
  const { selectionMode, selectedGameIds } = useAppSelector((state) => state.uiReducer);
  const { groups } = useAppSelector((state) => state.groupsReducer);
  const { params } = useAppSelector((state) => state.paramsReducer);
  const [busy, setBusy] = useState(false);

  const addable = useMemo(() => groups.filter((g) => g.CanAdd), [groups]);
  // The viewed group is the source for move/delete, when the user can modify it. No explicit group
  // means Home on the backend, so resolve it to Home here too.
  const current = useMemo(() => {
    if (params.group != null && params.group !== '') {
      return groups.find((g) => g.Token === params.group) ?? null;
    }
    return groups.find((g) => g.Kind === GroupKind.Home) ?? null;
  }, [groups, params.group]);
  const canModifyCurrent = current?.CanAdd ?? false;

  const hasGames = (games?.List?.length ?? 0) > 0;
  if (!isAuthenticated || (!selectionMode && !hasGames)) {
    return null;
  }

  const done = () => {
    dispatch(setSelectionMode(false));
    dispatch(fetchGroups());
    dispatch(getGames(params));
  };

  const run = async (fn: () => Promise<void>) => {
    if (selectedGameIds.length === 0) return;
    setBusy(true);
    try {
      await fn();
      done();
    } finally {
      setBusy(false);
    }
  };

  const copyTo = (g: GroupModel) => run(() => GroupsAPI.copyGames(g.Id, selectedGameIds));
  const moveTo = (g: GroupModel) => {
    if (
      !window.confirm(
        `Move ${selectedGameIds.length} game(s) to "${g.Name}"? This can't be undone.`,
      )
    )
      return;
    return run(() => GroupsAPI.moveGames(current!.Id, g.Id, selectedGameIds));
  };
  const deleteSelected = () => {
    if (!window.confirm(`Delete ${selectedGameIds.length} game(s)? This can't be undone.`)) return;
    return run(() => GamesAPI.deleteMany(selectedGameIds));
  };

  // Tick every game matching the current filter, across all pages — not just the visible page.
  const total = games?.Total ?? games?.List?.length ?? 0;
  const allSelected = total > 0 && selectedGameIds.length >= total;
  const selectAll = async () => {
    setBusy(true);
    try {
      const ids = await GamesAPI.getGameIds(params);
      dispatch(setSelectedGameIds(ids));
    } finally {
      setBusy(false);
    }
  };

  // Bulk selection is desktop-only.
  if (!selectionMode) {
    if (isMobile) return null;
    return (
      <div className="cm-bulk-bar">
        <Button
          variant="outline-secondary"
          size="sm"
          onClick={() => dispatch(setSelectionMode(true))}
        >
          <FontAwesomeIcon icon={faListCheck} className="me-2" />
          Select games
        </Button>
      </div>
    );
  }

  const count = selectedGameIds.length;
  const disabled = busy || count === 0;

  return (
    <div className="cm-bulk-bar cm-bulk-bar-active">
      <span className="cm-bulk-count">{count} selected</span>

      {total > count || (total > 0 && count === 0) ? (
        <Button
          variant="outline-secondary"
          size="sm"
          disabled={busy || allSelected}
          onClick={selectAll}
        >
          <FontAwesomeIcon icon={faListCheck} className="me-2" />
          Select all{total > 0 ? ` ${total}` : ''}
        </Button>
      ) : null}

      <Dropdown>
        <Dropdown.Toggle variant="primary" size="sm" disabled={disabled}>
          <FontAwesomeIcon icon={faCopy} className="me-2" />
          Copy to
        </Dropdown.Toggle>
        <Dropdown.Menu>
          {addable.map((g) => (
            <Dropdown.Item key={g.Id} onClick={() => copyTo(g)}>
              {g.Name}
            </Dropdown.Item>
          ))}
        </Dropdown.Menu>
      </Dropdown>

      {canModifyCurrent ? (
        <Dropdown>
          <Dropdown.Toggle variant="outline-secondary" size="sm" disabled={disabled}>
            <FontAwesomeIcon icon={faRightLeft} className="me-2" />
            Move to
          </Dropdown.Toggle>
          <Dropdown.Menu>
            {addable
              .filter((g) => g.Id !== current!.Id)
              .map((g) => (
                <Dropdown.Item key={g.Id} onClick={() => moveTo(g)}>
                  {g.Name}
                </Dropdown.Item>
              ))}
          </Dropdown.Menu>
        </Dropdown>
      ) : null}

      {canModifyCurrent ? (
        <Button variant="outline-danger" size="sm" disabled={disabled} onClick={deleteSelected}>
          <FontAwesomeIcon icon={faTrash} className="me-2" />
          Delete
        </Button>
      ) : null}

      <Button
        variant="link"
        size="sm"
        className="cm-bulk-cancel"
        disabled={busy}
        onClick={() => {
          dispatch(clearSelection());
          dispatch(setSelectionMode(false));
        }}
      >
        <FontAwesomeIcon icon={faXmark} className="me-1" />
        Cancel
      </Button>
    </div>
  );
}
